/**
 * RoleGroup — a named permission set INSIDE one Role, as it reaches the wire.
 *
 * The database model has existed since the NEX-2514 capability slice
 * (`RoleGroup` + `RoleGroupCapability` + `RoleGroupMember` + `RoleGroupSurface`)
 * and nothing on the API could reach it. This file is that missing half: the
 * view a Settings screen renders, and the request shapes that author one.
 *
 * TWO INDEPENDENT AXES, and conflating them is the mistake `schema.prisma`
 * already writes a paragraph to prevent — the same split is load-bearing here:
 *
 *   - `capabilities` — what a member may DO to the Role itself.
 *   - `resourceRelation` + `surfaces` — what a member may REACH through it.
 *
 * `resourceRelation: null` is a CAPABILITY-ONLY group and is a legitimate,
 * representable state — never a missing value to be defaulted. A writer that
 * silently substitutes `viewer` for it hands out resource access nobody asked
 * for.
 *
 * WHAT IS DELIBERATELY NOT HERE: the capability CATALOG. `ROLE_CAPABILITIES`,
 * `RoleCapability` and `PERMISSION_SET_TEMPLATES` are compile-time constants in this
 * same package with zero Prisma runtime dependency, so a frontend imports them
 * directly. An endpoint returning them would be a network round trip for a
 * value the bundle already contains, and a second source of truth for a closed
 * set.
 *
 * Frontend-safe: zero Prisma runtime dependency, same as its neighbours.
 */

import { z } from "zod";

import { Relation, RELATIONS } from "../permissions/permissions.types";
import {
  PERMISSION_SET_SURFACE_WILDCARD,
  type PermissionSetSurfaceValue,
  PermissionSetSurfaceValueSchema
} from "./permission-set-surface.types";
import {
  PermissionSetTemplateKeySchema,
  type RoleCapability,
  RoleCapabilitySchema
} from "./role-capability.types";

/**
 * The Relation a group confers on the Role's resources, or `null` for a
 * capability-only group.
 *
 * `.nullable()` rather than `.optional()` on purpose: the absence of resource
 * access is a value someone chose, and it has to survive a round trip through
 * JSON as itself. An optional key would let a client omit it and let a server
 * read the omission as "unchanged" or as "none" depending on who wrote the
 * handler — which is exactly the ambiguity the column's nullability was
 * designed to remove.
 */
export const PermissionSetResourceRelationSchema = z.enum(RELATIONS).nullable();

/**
 * A group's display name.
 *
 * The column is `@db.Text` and its only CHECK is `length(btrim(name)) > 0`, so
 * the ceiling here is a product decision rather than a storage one. It is
 * declared once and shared by create and update so the two cannot drift into
 * accepting different names for the same field.
 */
export const PERMISSION_SET_NAME_MAX_LENGTH = 120;

export const PermissionSetNameSchema = z
  .string()
  .trim()
  .min(1, "A permission set needs a name")
  .max(PERMISSION_SET_NAME_MAX_LENGTH);

/**
 * One permission set on one Role, as the API returns it.
 *
 * `memberCount` is a COUNT and not the member rows: the Settings list renders
 * "4 members" per group and the roster is a different screen, so shipping the
 * subject ids on every row would disclose org membership to anyone holding
 * `group.view` while paying for rows nothing draws.
 *
 * `key` is non-null exactly when `isSystem` is true — the database enforces the
 * biconditional (`RoleGroup_key_iff_system`), so a reader may rely on it.
 */
export const PermissionSetSchema = z.object({
  id: z.string(),
  roleId: z.string(),
  /** `maintainer` / `member` for a seeded template, `null` for a custom group. */
  key: PermissionSetTemplateKeySchema.nullable(),
  name: z.string(),
  /** Assem's "Ships with Nexus". Its capability set is immutable in the product. */
  isSystem: z.boolean(),
  resourceRelation: PermissionSetResourceRelationSchema,
  position: z.number().int().nonnegative(),
  /**
   * The capabilities this group holds.
   *
   * READS DROP WHAT THEY CANNOT RECOGNISE and writes refuse it — the asymmetry
   * `isRoleCapability` documents. A row written under a capability this build
   * has since removed must not grant anything, so it never reaches this array.
   */
  capabilities: z.array(RoleCapabilitySchema),
  /**
   * The surfaces the group's resource access is narrowed to.
   *
   * A STRICT ALLOW-LIST: an empty array reaches NOTHING, and `["*"]` reaches
   * every surface. Do not read the empty case as "unrestricted" — that
   * inversion is the exact failure `PERMISSION_SET_SURFACE_WILDCARD` exists to
   * prevent, and it is invisible in both directions.
   */
  surfaces: z.array(PermissionSetSurfaceValueSchema),
  memberCount: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string()
});

export type RoleGroup = z.infer<typeof PermissionSetSchema>;

/**
 * Collapse a caller's surface list to its canonical stored form.
 *
 * `*` and named surfaces may coexist without ambiguity — the set is the UNION
 * of the rows and `*` subsumes the rest — but storing both leaves two rows
 * saying one thing, and then "remove the inbox surface" produces a group that
 * still reaches the inbox. So the wildcard wins and the named rows go, which is
 * what `schema.prisma` means by "write paths normalise by deleting named rows
 * when the wildcard is set".
 *
 * Exported rather than hidden in a backend use case because the frontend needs
 * the identical answer to render what it just sent. Two implementations of one
 * normalisation is how an optimistic update starts disagreeing with the server.
 */
export const normalizePermissionSetSurfaces = (
  surfaces: readonly PermissionSetSurfaceValue[]
): PermissionSetSurfaceValue[] => {
  if (surfaces.includes(PERMISSION_SET_SURFACE_WILDCARD)) return [PERMISSION_SET_SURFACE_WILDCARD];
  return [...new Set(surfaces)];
};

/**
 * Deduplicate a caller's capability list.
 *
 * `RoleGroupCapability` is unique on `(permissionSetId, capability)`, so a repeated
 * entry is not a redundant no-op — it reaches Postgres as a 23505 and surfaces
 * as a 500, blaming the server for a request that merely said the same true
 * thing twice. A UI that toggles a checkbox twice, or a client that concatenates
 * two sets, produces exactly that.
 *
 * The SIBLING of {@link normalizePermissionSetSurfaces}, and it exists because the
 * asymmetry was the bug: surfaces were normalised and capabilities were not, on
 * two tables with the identical constraint. Two arrays with the same shape and
 * the same failure need the same treatment, or the one nobody remembered is the
 * one that 500s.
 *
 * Deduplicating rather than refusing: duplicates express no ambiguity — the set
 * is what the caller meant either way — so a 400 would be pedantry, where a
 * conflicting `resourceRelation` would genuinely need refusing.
 */
export const normalizePermissionSetCapabilities = (
  capabilities: readonly RoleCapability[]
): RoleCapability[] => [...new Set(capabilities)];

/**
 * True when this group's definition is owned by code rather than by the
 * customer.
 *
 * The product rule is "only MEMBERSHIP of a system group is editable", and this
 * is the one place it is spelled. Every write path asks this rather than
 * re-deriving `isSystem === true`, so the day a third template key appears the
 * question has one answer.
 */
export const isPermissionSetDefinitionEditable = (group: Pick<RoleGroup, "isSystem">): boolean =>
  !group.isSystem;

/**
 * The default a NEW custom group takes when the author states no relation.
 *
 * `null` — a capability-only group. Deliberately the least-granting value:
 * creating a permission set and forgetting the resource axis must not hand its
 * members editor access to every system in the Role.
 */
export const DEFAULT_PERMISSION_SET_RESOURCE_RELATION: Relation | null = null;
